import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';

const CancellationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  itemType, // 'invoice' or 'closed_order'
  itemNumber,
  loading = false,
  currentPaymentStatus = null // Current payment status of the item
}) => {
  const [reason, setReason] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');

  // Payment status options for cancellation
  const paymentStatusOptions = [
    { value: 'refund_initiated', label: 'Refund Initiated – Refund process started' },
    { value: 'refund_completed', label: 'Refund Completed – Refund processed successfully' },
    { value: 'partial_refund', label: 'Partially Refunded – Partial refund processed' },
    { value: 'no_payment', label: 'Cancelled (No Payment) – No refund needed' }
  ];

  // Reset payment status when modal opens to show placeholder
  useEffect(() => {
    if (isOpen) {
      // Reset to empty string to show placeholder "Select Payment Status"
      setPaymentStatus('');
      setReason('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    // Validate that payment status is selected
    if (!paymentStatus) {
      alert('Please select a payment status before confirming cancellation');
      return;
    }
    
    const confirmData = {
      reason,
      paymentStatus
    };
    console.log('🔴 CancellationModal handleConfirm - Sending data:', confirmData);
    console.log('🔴 Payment Status value:', paymentStatus);
    console.log('🔴 Reason value:', reason);
    onConfirm(confirmData);
  };

  const handleClose = () => {
    setReason('');
    setPaymentStatus('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="mb-6">
          <p className="text-slate-700 mb-4">
            Are you sure you want to cancel this {itemType === 'invoice' ? 'invoice' : 'closed order'}?
          </p>
          
          {itemNumber && (
            <div className="bg-white rounded-lg p-3 mb-4">
              <span className="text-sm font-medium text-slate-600">
                {itemType === 'invoice' ? 'Invoice Number:' : 'Closed Order Number:'} 
              </span>
              <span className="text-sm font-semibold text-slate-900 ml-2">
                {itemNumber}
              </span>
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-yellow-800">
              <strong>Warning:</strong> This action cannot be undone. The {itemType === 'invoice' ? 'invoice' : 'closed order'} will be marked as cancelled and:
            </p>
            <ul className="text-sm text-yellow-700 mt-2 ml-4 list-disc">
              <li>Status will change to "Cancelled"</li>
              <li>Products will be returned to inventory</li>
              <li>No further edits will be allowed</li>
            </ul>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Payment Status After Cancellation
              </label>
              <select
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
              >
                <option value="">Select payment status...</option>
                {paymentStatusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Reason for Cancellation
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                rows="3"
                placeholder="Enter reason for cancellation..."
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <button
            onClick={handleClose}
            disabled={loading}
            className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-white transition-colors disabled:opacity-50"
          >
            Close
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Cancelling...
              </>
            ) : (
              'Confirm Cancel'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancellationModal;

